import {
  Breadcrumbs,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  Grid,
  makeStyles,
  Typography,
} from "@material-ui/core";
import { InfoOutlined } from "@material-ui/icons";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import clsx from "clsx";
import React, { useMemo } from "react";
import { Link, Redirect } from "react-router-dom";

import Navigation from "../Navigation";
import BlockerStalenessBanner from "./BlockerStalenessBanner";
import useFetchLatestReport from "./queries/useFetchLatestReport";
import BlockedTrackersTable from "./TrackerBlockingPage/BlockedTrackersTable";
import DisableTrackerBlockerButton from "./TrackerBlockingPage/DisableTrackerBlockerButton";
import EnableTrackerBlockerButton from "./TrackerBlockingPage/EnableTrackerBlockerButton";
import PublishTrackerBlockerButton from "./TrackerBlockingPage/PublishTrackerBlockerButton";
import useTrackerBlockingStaleness from "./useTrackerBlockingStaleness";
import { resolutionTypeAdjective } from "./WebTrackerDetailsPage/TrackerResolution";
import useWTMURLs from "./useWTMURLs";

const useStyles = makeStyles((theme) => ({
  blockerButton: {
    height: theme.spacing(6),
    marginLeft: theme.spacing(1),
  },
  breadcrumb: {
    marginTop: 5,
    marginBottom: 10,
  },
  breadcrumbLink: {
    cursor: "pointer",
  },
  card: {
    width: "100%",
    minHeight: 500,
  },
  cardContent: {
    padding: 0,
    "&:last-child": {
      paddingBottom: 0,
    },
  },
  cardHeader: {
    backgroundColor: theme.palette.grey[50],
    borderBottomColor: theme.palette.grey[200],
    borderBottomStyle: "solid",
    borderBottomWidth: 1,
    padding: theme.spacing(2),
  },
  darkText: {
    color: "#263238",
  },
  disableButton: {
    background: theme.palette.error.main,
    color: "white",
    "&:hover": {
      background: theme.palette.error.dark,
    },
  },
  info: {
    marginTop: theme.spacing(1),
  },
  infoIcon: {
    fontSize: theme.typography.fontSize,
    marginTop: -theme.spacing(0.5),
  },
  link: {
    cursor: "pointer",
    textDecoration: "underline",
  },
  loader: {
    left: "50%",
    position: "absolute",
    top: "50%",
    transform: "translateX(-50%) translateY(-50%)",
  },
  navigation: {
    paddingTop: 12,
  },
  pageTitle: {
    paddingTop: 12,
  },
}));

function TrackerBlockingPage() {
  const classes = useStyles();
  const wtmURLs = useWTMURLs();

  const {
    isLoading: latestReportLoading,
    isError: latestReportError,
    data: latestReport,
  } = useFetchLatestReport();

  const {
    isLoading: trackerBlockingStalenessLoading,
    isError: stalenessError,
    isResolutionPublished,
    staleness,
    trackersResolvedWithBlocking,
  } = useTrackerBlockingStaleness();

  const isLoading = latestReportLoading || trackerBlockingStalenessLoading;
  const isError = latestReportError || stalenessError;

  const rows = useMemo(() => {
    if (isLoading || isError || latestReport === undefined) {
      return [];
    }

    return trackersResolvedWithBlocking.map((t) => {
      const trackerStats = latestReport.reportTrackerStats.find(
        (stats) => stats.tracker === t.name
      );

      return {
        webTracker: t,
        isPublished:
          t.resolution !== null && isResolutionPublished(t.resolution),
        numPages: trackerStats?.approxNumPages ?? null,
        lastDetectedAt:
          trackerStats?.lastDetectedAt ?? trackerStats?.firstDetectedAt ?? null,
        resolution: t.resolution,
      };
    });
  }, [
    trackersResolvedWithBlocking,
    latestReport,
    isLoading,
    isError,
    isResolutionPublished,
  ]);

  let cardContents: JSX.Element | string | null;
  if (isError) {
    cardContents =
      "Something went wrong. Try refreshing the page, or contact support@freshpaint.io for assistance.";
  } else if (isLoading) {
    cardContents = (
      <div className={classes.loader}>
        <CircularProgress />
      </div>
    );
  } else if (latestReport === undefined || staleness === null) {
    // in this case, the current environment is not setup for web tracker
    // monitoring. redirect to trackers overview page where the empty state will clarify
    cardContents = <Redirect to={wtmURLs.trackersOverview} />;
  } else {
    cardContents = <BlockedTrackersTable rows={rows} />;
  }

  return (
    <Navigation className={classes.navigation}>
      <BlockerStalenessBanner />

      <div className={classes.pageTitle}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          className={classes.breadcrumb}
        >
          <Link
            className={classes.breadcrumbLink}
            to={wtmURLs.trackersOverview}
          >
            <Typography variant="overline">Web Tracker Manager</Typography>
          </Link>
          <Typography className={clsx(classes.darkText)} variant="overline">
            Tracker Blocking
          </Typography>
        </Breadcrumbs>

        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Typography component="h1" gutterBottom variant="h3">
              Tracker Blocking
            </Typography>
            <Typography variant="subtitle1" gutterBottom>
              Block trackers on your site.
            </Typography>
          </Grid>
          <Grid container item xs={6} justifyContent="flex-end">
            {(() => {
              if (isLoading || staleness === null) {
                return null;
              }

              if (staleness === "disabled") {
                return (
                  <EnableTrackerBlockerButton
                    className={classes.blockerButton}
                    fullWidth={false}
                    size="large"
                  />
                );
              }

              return (
                <DisableTrackerBlockerButton
                  className={clsx(classes.blockerButton, classes.disableButton)}
                  fullWidth={false}
                  size="large"
                />
              );
            })()}
            <PublishTrackerBlockerButton
              className={classes.blockerButton}
              fullWidth={false}
              size="large"
            />
          </Grid>
        </Grid>
      </div>

      <Card className={classes.card}>
        <CardHeader
          className={classes.cardHeader}
          title="Blocked Trackers"
          subheader={
            <Typography className={classes.info} variant="body1">
              <InfoOutlined className={classes.infoIcon} /> Add trackers to your
              site&apos;s blocklist by resolving them in the{" "}
              <Link className={classes.link} to={wtmURLs.trackersOverview}>
                Web Tracker Manager
              </Link>{" "}
              with "{resolutionTypeAdjective("tracker_blocked")}", then publish
              the blocklist to your site by clicking <strong>Publish</strong>{" "}
              above.
            </Typography>
          }
        />

        <Divider />

        <CardContent className={classes.cardContent}>
          {cardContents}
        </CardContent>
      </Card>
    </Navigation>
  );
}

export default TrackerBlockingPage;
